import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gauge, Loader2, Save } from "lucide-react";

type Settings = { max_concurrency: number; max_per_minute: number };

export function HelpdeskAlertRetrySettingsPanel() {
  const [s, setS] = useState<Settings>({ max_concurrency: 1, max_per_minute: 30 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_alert_retry_settings" as any)
      .select("max_concurrency, max_per_minute")
      .eq("id", true)
      .maybeSingle();
    setLoading(false);
    if (error) return toast.error(error.message);
    if (data) setS(data as unknown as Settings);
  };

  useEffect(() => { void load(); }, []);

  const save = async () => {
    if (s.max_concurrency < 1 || s.max_concurrency > 20) return toast.error("Concurrency must be 1–20");
    if (s.max_per_minute < 1 || s.max_per_minute > 600) return toast.error("Rate limit must be 1–600/min");
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("helpdesk_alert_retry_settings" as any)
      .update({
        max_concurrency: s.max_concurrency,
        max_per_minute: s.max_per_minute,
        updated_at: new Date().toISOString(),
        updated_by: user?.id ?? null,
      })
      .eq("id", true);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Retry limits saved");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="size-4 text-primary" /> Bulk retry limits
        </CardTitle>
        <CardDescription>
          Controls how aggressively bulk retry batches hit downstream Slack webhooks. Single-row retries always run at 1× and ignore these limits.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 text-center"><Loader2 className="size-4 animate-spin inline" /></div>
        ) : (
          <div className="grid gap-3 md:grid-cols-[160px_160px_auto] items-end">
            <div className="space-y-1">
              <Label className="text-xs">Max concurrency</Label>
              <Input type="number" min={1} max={20} value={s.max_concurrency}
                onChange={(e) => setS({ ...s, max_concurrency: Number(e.target.value) || 1 })} />
              <p className="text-[11px] text-muted-foreground">Parallel deliveries per batch (1–20).</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Max per minute</Label>
              <Input type="number" min={1} max={600} value={s.max_per_minute}
                onChange={(e) => setS({ ...s, max_per_minute: Number(e.target.value) || 30 })} />
              <p className="text-[11px] text-muted-foreground">Global token bucket (1–600/min).</p>
            </div>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              <span className="ml-1">Save</span>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default HelpdeskAlertRetrySettingsPanel;
